/**
 * Dokumente eines Kunden.
 *
 * Der Inhalt liegt nicht auf dem Server, sondern in der eingestellten
 * Ablage – SharePoint, Nextcloud oder Storage-Server. Für die Bedienung
 * ändert das nichts: hochladen, ansehen, löschen.
 *
 * Ein Punkt muss hier immer sichtbar sein: ob der Kunde das Dokument in
 * seinem Portal sieht. Voreinstellung ist nein. Eine interne Kalkulation
 * und ein Vertrag liegen in derselben Liste, und der Unterschied darf sich
 * nicht erst beim Klick zeigen.
 */

import { useRef, useState } from 'react'
import {
  DOKUMENTARTTEXT,
  bytesLesbar,
  dokumentAdresse,
  useDokumentAendern,
  useDokumentHochladen,
  useDokumentLoeschen,
  useDokumente,
  type Dokument,
  type Dokumentart,
} from '../api/kundenakte'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from '../bausteine/Hinweise'
import { darfSchreiben, useKonto } from '../konto/KontoKontext'

const ARTEN = Object.keys(DOKUMENTARTTEXT) as Dokumentart[]

export function Dokumente({ kundeId }: { kundeId: string }) {
  const { konto } = useKonto()
  const liste = useDokumente(kundeId)
  const hochladen = useDokumentHochladen(kundeId)
  const aendern = useDokumentAendern(kundeId)
  const loeschen = useDokumentLoeschen(kundeId)
  const dateifeld = useRef<HTMLInputElement>(null)
  const [art, setzeArt] = useState<Dokumentart>('vertrag')
  const [imPortal, setzeImPortal] = useState(false)

  const schreiben = darfSchreiben(konto)

  async function beimAbschicken(ereignis: React.FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    const datei = dateifeld.current?.files?.[0]
    if (!datei) return

    const formular = new FormData()
    formular.append('datei', datei)
    formular.append('art', art)
    formular.append('im_portal_sichtbar', String(imPortal))
    await hochladen.mutateAsync(formular)

    // Zurücksetzen, sonst lädt der nächste Klick dieselbe Datei erneut hoch.
    if (dateifeld.current) dateifeld.current.value = ''
    setzeImPortal(false)
  }

  if (liste.isLoading) return <Ladeanzeige />

  return (
    <div className="space-y-4">
      <Fehlerhinweis fehler={liste.error ?? hochladen.error ?? loeschen.error} />

      {schreiben && (
        <form onSubmit={(e) => void beimAbschicken(e)} className="karte space-y-3">
          <div>
            <label htmlFor="datei">Datei</label>
            <input
              ref={dateifeld}
              id="datei"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.tif,.tiff,.doc,.docx,.xls,.xlsx,.txt,.csv"
              required
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="art">Art</label>
              <select
                id="art"
                value={art}
                onChange={(e) => setzeArt(e.target.value as Dokumentart)}
              >
                {ARTEN.map((a) => (
                  <option key={a} value={a}>
                    {DOKUMENTARTTEXT[a]}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-start gap-3 self-end pb-1 font-normal">
              <input
                type="checkbox"
                checked={imPortal}
                onChange={(e) => setzeImPortal(e.target.checked)}
                className="mt-1 h-6 w-6 shrink-0"
                style={{ minHeight: 'auto' }}
              />
              <span>
                <span className="font-semibold">Der Kunde darf es sehen</span>
                <span className="block text-base text-slate-600">
                  Erscheint im Kundenportal. Ohne Haken bleibt es intern.
                </span>
              </span>
            </label>
          </div>

          <button type="submit" className="knopf-haupt" disabled={hochladen.isPending}>
            {hochladen.isPending ? 'Lädt hoch …' : 'Hochladen'}
          </button>
        </form>
      )}

      {liste.data?.length === 0 ? (
        <p className="text-slate-600">Noch keine Unterlagen zu diesem Kunden.</p>
      ) : (
        <ul className="space-y-2">
          {liste.data?.map((d) => (
            <Zeile
              key={d.id}
              dokument={d}
              kundeId={kundeId}
              schreiben={schreiben}
              umschalten={() =>
                aendern.mutate({ id: d.id, im_portal_sichtbar: !d.im_portal_sichtbar })
              }
              entfernen={() => loeschen.mutate(d.id)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function Zeile({
  dokument,
  kundeId,
  schreiben,
  umschalten,
  entfernen,
}: {
  dokument: Dokument
  kundeId: string
  schreiben: boolean
  umschalten: () => void
  entfernen: () => void
}) {
  const [sicher, setzeSicher] = useState(false)

  return (
    <li className="karte flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <a
          href={dokumentAdresse(kundeId, dokument.id)}
          target="_blank"
          rel="noreferrer"
          className="block font-semibold text-akzent-700 underline"
        >
          {dokument.name}
        </a>
        <p className="text-base text-slate-600">
          {DOKUMENTARTTEXT[dokument.art]} · {bytesLesbar(dokument.groesse)} ·{' '}
          {datumDeutsch(dokument.erstellt_am)}
          {dokument.hochgeladen_von ? ` · ${dokument.hochgeladen_von}` : ''}
        </p>
        {dokument.bemerkung && (
          <p className="mt-1 text-base text-slate-700">{dokument.bemerkung}</p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`marke ${
            dokument.im_portal_sichtbar
              ? 'bg-stufe-ob-grund text-stufe-ob'
              : 'bg-slate-100 text-slate-700'
          }`}
        >
          {dokument.im_portal_sichtbar ? 'Kunde sieht es' : 'Intern'}
        </span>

        {schreiben && (
          <>
            <button type="button" className="knopf-still px-3" onClick={umschalten}>
              {dokument.im_portal_sichtbar ? 'Intern stellen' : 'Freigeben'}
            </button>
            {sicher ? (
              <>
                <button type="button" className="knopf-warnung px-3" onClick={entfernen}>
                  Wirklich löschen
                </button>
                <button
                  type="button"
                  className="knopf-still px-3"
                  onClick={() => setzeSicher(false)}
                >
                  Abbrechen
                </button>
              </>
            ) : (
              <button
                type="button"
                className="knopf-still px-3"
                onClick={() => setzeSicher(true)}
              >
                Löschen
              </button>
            )}
          </>
        )}
      </div>
    </li>
  )
}
